import { ApolloClient, HttpLink, InMemoryCache, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';

import { authLink } from './auth';
import { isUnauthorizedNetworkError, notifyAuthExpired } from './auth-expiry';
import { typePolicies } from './cache-policies';
import { notifyNetworkError } from './error-toast';
import { isStaleBundleGraphQLError, reloadForStaleBundleOnce } from './stale-bundle';
import { env } from '../config/env';
import { logger } from '../lib/logger';

/**
 * Apollo Client setup for apps/web.
 *
 * Link chain (outermost → innermost):
 *   authLink  → injects the Auth0 Bearer token per-request (see auth.ts)
 *   errorLink → logs GraphQL + network errors; on a 401 it hands off to the
 *               central auth-expiry handler (re-login) instead of letting every
 *               page render a stuck error (see auth-expiry.ts)
 *   httpLink  → sends the request to the API
 *
 * The token getter inside authLink is installed by `useInstallAuthLink`
 * (called from AuthLinkInstaller in Providers.tsx) once the Auth0Provider
 * has booted. Until then, requests are forwarded without a token.
 */
const httpLink = new HttpLink({
  uri: env.VITE_GRAPHQL_ENDPOINT,
  credentials: 'include',
});

// `graphQLErrors` / `networkError` will be consolidated into a single `error`
// property in Apollo Client 4.x. We still target Apollo Client 3.12 here, so
// these are the supported fields. Suppress the forward-looking deprecation
// hint until the project upgrades to v4.
// eslint-disable-next-line @typescript-eslint/no-deprecated
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach((err) => {
      logger.error('[apollo] graphql error', {
        operation: operation.operationName,
        message: err.message,
        code: err.extensions?.code,
      });
    });
    // Stale-bundle self-heal: a validation error for a field/type the current
    // schema no longer defines means this tab is running an SPA bundle older
    // than the deploy. Reload once (guarded) to pull the fresh bundle instead
    // of silently rendering an empty page. See stale-bundle.ts.
    if (graphQLErrors.some(isStaleBundleGraphQLError)) {
      logger.warn('[apollo] stale bundle detected — reloading to the deployed version', {
        operation: operation.operationName,
      });
      reloadForStaleBundleOnce();
    }
  }
  if (networkError) {
    // A 401 means the session is no longer valid (expired / refused token).
    // Hand it to the central auth-expiry handler (re-login) instead of leaving
    // every page to render a stuck error under `errorPolicy: 'all'`.
    if (isUnauthorizedNetworkError(networkError)) {
      logger.warn('[apollo] unauthorized — session expired, re-authenticating', {
        operation: operation.operationName,
      });
      notifyAuthExpired();
    } else {
      logger.error('[apollo] network error', {
        operation: operation.operationName,
        message: networkError.message,
      });
      // Surface the failure to the user via the app-root toast (throttled;
      // localized copy lives with the handler — see AppToastProvider).
      notifyNetworkError();
    }
  }
});

export const apolloClient = new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache({ typePolicies }),
  defaultOptions: {
    watchQuery: { fetchPolicy: 'cache-and-network', errorPolicy: 'all' },
    query: { fetchPolicy: 'network-only', errorPolicy: 'all' },
  },
});
